import { createPublicAssetClient } from '@/context/ClientContext'
import { useConfig } from '@/context/ConfigContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  DownloadJobStatus,
  GetShareDownloadResponse,
  GetShareInfoResponse,
} from '@droidmap/asset-service-contract'

export type ShareDownloadProgress = {
  percent: number
  processedAssets: number
  totalAssets: number
  processedBytes: number
  totalBytes: number
}

export type UseShareDownloadResult = {
  shareInfo: GetShareInfoResponse | null
  isLoadingInfo: boolean
  /** The link is unknown, expired, or revoked — shown as one generic state. */
  isLinkUnavailable: boolean
  startDownload: () => void
  isDownloading: boolean
  downloadJob: GetShareDownloadResponse | null
  progress: ShareDownloadProgress | null
  error: Error | null
}

/**
 * Public (unauthenticated) mirror of `useAssetSetDownload` for the
 * `/share/$token` page: load the link's public info, then on demand
 * initiate/reuse the staged ZIP job, poll it, and auto-navigate to the
 * presigned URL when it is ready. All calls ride an anonymous client — the
 * share token in the path is the credential.
 */
export function useShareDownload(token: string): UseShareDownloadResult {
  const { ENV } = useConfig()
  const assetClient = useMemo(() => createPublicAssetClient(ENV), [ENV])
  const queryClient = useQueryClient()
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null)
  // A guard, not state: it is never rendered, and only exists so the auto-download
  // navigation fires once per job. As state it forced an extra render on every
  // completed job for a value nothing reads.
  const hasAutoDownloadedRef = useRef(false)

  const infoQuery = useQuery({
    queryKey: ['shareInfo', token],
    queryFn: async () => assetClient.getShareInfo({ token }),
    retry: false,
  })

  const initiateMutation = useMutation({
    mutationFn: async () => assetClient.initiateShareDownload({ token }),
    onSuccess: (job) => {
      setDownloadJobId(job.id)
      hasAutoDownloadedRef.current = false
    },
  })

  const statusQuery = useQuery({
    queryKey: ['shareDownloadJob', token, downloadJobId],
    queryFn: async () => {
      if (!downloadJobId) return null
      return await assetClient.getShareDownloadJob({ token, downloadJobId })
    },
    enabled: !!downloadJobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 5000
      if (
        data.status === DownloadJobStatus.Completed ||
        data.status === DownloadJobStatus.Failed
      ) {
        return false
      }
      return 5000
    },
  })

  // Auto-download when ready — the presigned URL carries
  // Content-Disposition: attachment, so navigation triggers a download.
  useEffect(() => {
    if (
      statusQuery.data?.status === DownloadJobStatus.Completed &&
      statusQuery.data.downloadUrl &&
      !hasAutoDownloadedRef.current
    ) {
      hasAutoDownloadedRef.current = true
      window.location.href = statusQuery.data.downloadUrl
    }
  }, [statusQuery.data])

  // Derived, not stored: a failed job *is* the error, so there is nothing to
  // keep in sync. Storing it in an effect meant one render showed a failed job
  // with `error: null` before the effect re-rendered with the message.
  const failureError =
    statusQuery.data?.status === DownloadJobStatus.Failed
      ? new Error('Download failed. Please try again.')
      : null

  const progress: ShareDownloadProgress | null =
    statusQuery.data && statusQuery.data.totalAssets
      ? (() => {
          const processedBytes = statusQuery.data.processedBytes || 0
          const totalBytes = statusQuery.data.totalBytes || 0
          const percent =
            totalBytes > 0 ? Math.round((processedBytes / totalBytes) * 100) : 0
          return {
            percent,
            processedAssets: statusQuery.data.processedAssets,
            totalAssets: statusQuery.data.totalAssets,
            processedBytes,
            totalBytes,
          }
        })()
      : null

  const startDownload = useCallback(() => {
    setDownloadJobId(null)
    hasAutoDownloadedRef.current = false
    queryClient.removeQueries({ queryKey: ['shareDownloadJob'] })
    initiateMutation.mutate()
  }, [initiateMutation, queryClient])

  const isDownloading =
    initiateMutation.isPending ||
    (downloadJobId !== null &&
      statusQuery.data?.status !== DownloadJobStatus.Completed &&
      statusQuery.data?.status !== DownloadJobStatus.Failed)

  return {
    shareInfo: infoQuery.data ?? null,
    isLoadingInfo: infoQuery.isLoading,
    isLinkUnavailable: infoQuery.isError,
    startDownload,
    isDownloading,
    downloadJob: statusQuery.data ?? null,
    progress,
    error: (initiateMutation.error as Error | null) || failureError,
  }
}
